import React, { useMemo, useState } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Input,
  FormGroup,
  Label
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import BloggingDashboard from "../../blogging";
import IntlMessages from "Util/IntlMessages";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";

function PostsPipelinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.blogging.listPosts(),
    []
  );
  const cols = useMemo(() => {
    const list = data || [];
    return {
      Draft: list.filter(p => p.status === "draft"),
      Scheduled: list.filter(p => p.status === "scheduled"),
      Published: list.filter(p => p.status === "published")
    };
  }, [data]);

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.blogging.posts" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Editorial pipeline — drag cards between columns with your DnD layer.
        </p>
      }
    >
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {Object.entries(cols).map(([label, posts]) => (
            <Col md="6" key={label}>
              <h6 className="text-uppercase text-muted mb-3">{label}</h6>
              {posts.map(p => (
                <Card key={p.id} className="mb-3 border-0 shadow-sm">
                  <CardBody>
                    <div className="d-flex justify-content-between">
                      <strong>{p.title}</strong>
                      <Badge color="light" className="text-dark">
                        {p.views} views
                      </Badge>
                    </div>
                    <div className="small text-muted">{p.author}</div>
                    {p.editorialStage && (
                      <Badge color="info" className="me-1 mt-1">
                        {p.editorialStage}
                      </Badge>
                    )}
                    {p.scheduledFor && (
                      <div className="small text-primary mt-1">
                        Goes live {p.scheduledFor}
                      </div>
                    )}
                    {p.wordCount != null && (
                      <div className="small text-muted">
                        {p.wordCount.toLocaleString()} words
                      </div>
                    )}
                    <Button
                      tag={Link}
                      color="link"
                      size="sm"
                      className="px-0"
                      to={p.id}
                      relative="path"
                    >
                      Open in editor →
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function PostEditorPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.blogging.getPost(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.blogging.post-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col lg="8">
              <FormGroup>
                <Label>Title</Label>
                <Input type="text" defaultValue={data.title} />
              </FormGroup>
              <FormGroup>
                <Label>Body</Label>
                <Input
                  type="textarea"
                  rows={12}
                  defaultValue={`# ${data.title}\n\nCompose in your rich-text editor route or embed Quill here.`}
                />
              </FormGroup>
            </Col>
            <Col lg="4">
              <Card className="bg-light border-0">
                <CardBody>
                  <CardTitle tag="h6">Publish</CardTitle>
                  <Badge className="mb-2">{data.status}</Badge>
                  <p className="small text-muted">Author: {data.author}</p>
                  {data.scheduledFor && (
                    <p className="small">
                      Scheduled: <strong>{data.scheduledFor}</strong>
                    </p>
                  )}
                  {data.editorialStage && (
                    <p className="small text-muted">
                      Stage: {data.editorialStage}
                    </p>
                  )}
                  <Button color="primary" block size="sm">
                    Save draft
                  </Button>
                  <Button color="success" outline block size="sm" className="mt-2">
                    Schedule publish (wire)
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function CategoriesTagsPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.blogging.listCategories(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.blogging.categories" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <div className="d-flex flex-wrap gap-2">
          {(data || []).map(c => (
            <Button key={c.id} color="outline-primary" className="rounded-pill">
              {c.name}{" "}
              <Badge color="secondary" className="ms-1">
                {c.posts}
              </Badge>
            </Button>
          ))}
        </div>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function MediaLibraryGridPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.blogging.listMedia(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.blogging.media" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(m => (
            <Col xs="6" md="4" lg="3" key={m.id} className="mb-4">
              <Card className="text-center border-0 shadow-sm h-100">
                <CardBody>
                  <i className="simple-icon-doc h1 text-muted d-block mb-2" />
                  <div className="small text-truncate">{m.name}</div>
                  <Badge color="light" className="text-dark mt-1">
                    {m.sizeMb != null ? `${m.sizeMb} MB` : `${m.sizeKb} KB`}
                  </Badge>
                  {m.dimensions && (
                    <div className="small text-muted mt-1">{m.dimensions}</div>
                  )}
                  {m.durationMin != null && (
                    <div className="small text-muted mt-1">{m.durationMin} min</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function CommentsModerationPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.blogging.listComments(),
    []
  );
  const { clean, spam } = useMemo(() => {
    const list = data || [];
    return {
      clean: list.filter(c => c.status !== "spam"),
      spam: list.filter(c => c.status === "spam")
    };
  }, [data]);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <SuitePageShell heading={<IntlMessages id="suite.blogging.comments" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          <Col md="6">
            <h6 className="text-success mb-3">Queue</h6>
            {clean.map(c => (
              <Card key={c.id} className="mb-2 border-0 shadow-sm">
                <CardBody className="py-2">
                  <div className="d-flex justify-content-between">
                    <div className="small text-muted">{c.post}</div>
                    <Badge color="light" className="text-dark">
                      {c.status}
                    </Badge>
                  </div>
                  <div className="font-weight-medium">{c.author}</div>
                  <div className="small">{c.body}</div>
                  {(c.replies || 0) > 0 && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 mt-1"
                      onClick={() =>
                        setExpanded(expanded === c.id ? null : c.id)
                      }
                    >
                      {c.replies} repl
                      {c.replies === 1 ? "y" : "ies"} (thread demo)
                    </Button>
                  )}
                  {expanded === c.id && (
                    <div className="small bg-light rounded p-2 mt-2 border-start border-primary ps-2">
                      Nested replies would load from your comments API.
                    </div>
                  )}
                  <div className="mt-2">
                    <Button color="success" size="sm" className="me-1">
                      Approve
                    </Button>
                    <Button color="outline-danger" size="sm">
                      Reject
                    </Button>
                    {c.status === "pending" && (
                      <Button color="outline-secondary" size="sm" className="ms-1">
                        Escalate
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </Col>
          <Col md="6">
            <h6 className="text-danger mb-3">Spam</h6>
            {spam.map(c => (
              <Card key={c.id} className="mb-2 border-danger border bg-light">
                <CardBody className="py-2 small">{c.body}</CardBody>
              </Card>
            ))}
          </Col>
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function BloggingSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="blogging" />}>
        <Route index element={<BloggingDashboard />} />
        <Route path="posts" element={<PostsPipelinePage />} />
        <Route path="posts/:id" element={<PostEditorPage />} />
        <Route path="categories" element={<CategoriesTagsPage />} />
        <Route path="media" element={<MediaLibraryGridPage />} />
        <Route path="comments" element={<CommentsModerationPage />} />
      </Route>
    </Routes>
  );
}
